import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import UploadProgress from "@/components/UploadProgress";
import { UploadSimple, FileText } from "@phosphor-icons/react";

const DOC_TYPES = ["Right to Work", "Identification", "Certificate", "Qualification", "Training", "DBS Check"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("Right to Work");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/staff/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", type);
      await api.post("/staff/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress((evt.loaded / evt.total) * 100);
        },
      });
      setProgress(100);
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please retry in a moment.");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(null), 800);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="label-caps">Compliance</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Documents</h1>
        <p className="text-slate-600 mt-1">Upload identification, right-to-work and certifications. Admin will verify each.</p>
      </div>

      <div className="card-tactile p-5 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <div className="label-caps mb-1">Document type</div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="doc-type-select" className="h-12"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="doc-file-input" />
          <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 rounded-md h-12 px-6">
            <UploadSimple size={18} className="mr-2" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
        <UploadProgress percent={progress} label={busy ? "Uploading document" : "Upload complete"} />
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <div className="text-slate-500 py-8 text-center">No documents uploaded yet.</div>}
        {docs.map((d) => (
          <div key={d.id} className="card-tactile p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={22} className="text-blue-600 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{d.document_type}</div>
                <div className="text-xs text-slate-500 truncate">{d.original_filename}</div>
              </div>
            </div>
            <StatusBadge status={d.verification_status} />
          </div>
        ))}
      </div>
    </div>
  );
}
